import { type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { FolderOpen, Check, Sparkles } from "lucide-react";
import { cn } from "@/modern/utils";
import { FILE_DETAIL_FIELD_LABEL_INLINE_CLS } from "../../fileDetailFormClasses";

/** Minimal tag shape — MarketingTag and promote-surface content-role rows both qualify. */
export type FolderSelectableTag = {
  id: number;
  label: string;
  description?: string | null;
};

interface MarketingFolderSelectorProps {
  folderTags: FolderSelectableTag[];
  selectedTagIds: number[];
  onToggle: (tagId: number) => void;
  getImageCount?: (tagId: number) => number;
  isAiSuggested?: (tagId: number) => boolean;
  renderExpandedContent?: (tag: FolderSelectableTag) => ReactNode;
  /** Renders on the same row as the section title (e.g. step help). */
  headerEnd?: ReactNode;
}

export const MarketingFolderSelector = ({
  folderTags,
  selectedTagIds,
  onToggle,
  getImageCount,
  isAiSuggested,
  renderExpandedContent,
  headerEnd,
}: MarketingFolderSelectorProps) => {
  if (folderTags.length === 0 && headerEnd == null) return null;

  const headerBlock = (
    <div className="mb-1.5 flex min-h-7 items-center justify-between gap-2">
      <label className={FILE_DETAIL_FIELD_LABEL_INLINE_CLS}>Content role</label>
      {headerEnd != null ? <div className="flex shrink-0 items-center">{headerEnd}</div> : null}
    </div>
  );

  if (folderTags.length === 0) {
    return <div>{headerBlock}</div>;
  }

  return (
    <div>
      {headerBlock}
      <div className="space-y-1.5">
        {folderTags.map((tag) => {
          const isSelected = selectedTagIds.some((id) => Number(id) === Number(tag.id));
          const imageCount = getImageCount?.(tag.id);
          const aiSuggested = isAiSuggested?.(tag.id) ?? false;
          const expandedContent = isSelected && renderExpandedContent ? renderExpandedContent(tag) : null;

          return (
            <div
              key={tag.id}
              className={cn(
                "overflow-hidden rounded-lg border transition-colors duration-150",
                isSelected
                  ? "border-primary bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary)/0.3)]"
                  : "border-border hover:border-muted-foreground/40",
              )}
            >
              <button
                type="button"
                onClick={() => onToggle(tag.id)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-2.5 text-left transition-colors duration-150",
                  isSelected ? "bg-primary/10" : "bg-transparent hover:bg-muted/30",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5">
                    <FolderOpen
                      className={cn(
                        "size-4 shrink-0 transition-colors duration-150",
                        isSelected ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <span
                      className={cn(
                        "truncate text-sm font-normal transition-colors",
                        isSelected ? "text-primary" : "text-foreground",
                      )}
                    >
                      {tag.label}
                    </span>
                    {imageCount != null ? (
                      <span
                        className={cn(
                          "inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-normal tabular-nums leading-none transition-colors",
                          imageCount > 0
                            ? "bg-primary/20 text-primary ring-1 ring-inset ring-primary/35"
                            : "bg-muted text-muted-foreground ring-1 ring-inset ring-border/70",
                        )}
                      >
                        {imageCount}
                      </span>
                    ) : null}
                    {aiSuggested ? (
                      <span className="inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-normal bg-violet-500/15 text-violet-400">
                        <Sparkles className="size-2.5" />
                        AI
                      </span>
                    ) : null}
                  </div>
                  {tag.description ? (
                    <span className="mt-0.5 block truncate pl-[1.625rem] text-xs text-muted-foreground/70">
                      {tag.description}
                    </span>
                  ) : null}
                </div>

                <div
                  className={cn(
                    "flex size-4 shrink-0 items-center justify-center rounded transition-all duration-150",
                    isSelected
                      ? "bg-primary"
                      : "border border-muted-foreground/25 bg-transparent",
                  )}
                >
                  {isSelected ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 600, damping: 25 }}
                    >
                      <Check className="size-3 text-primary-foreground" />
                    </motion.div>
                  ) : null}
                </div>
              </button>

              <AnimatePresence initial={false}>
                {expandedContent ? (
                  <motion.div
                    key="expanded"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden border-t border-border/60"
                  >
                    {expandedContent}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};
